/*!
 * Copyright (c) 2026 Elevation Technologies Pte Ltd.
 * Developer: filimoni@elevationtech.com.fj
 */
import React, { useEffect, useState } from 'react';
import { FSF_BRAND } from '../fsfPages.generated';
import PhotoLightbox from '../components/PhotoLightbox';
import VideoFrame from '../components/VideoFrame';
import {
  CUSTOMER_REVIEW_URL,
  GOOGLE_BUSINESS_URL,
  HOME_IMAGES,
  HOME_PHOTOS,
  HOME_SLIDESHOW_PHOTOS,
  HOME_REVIEWS,
  HOME_SERVICE_POST_SLUGS,
  TRACK_SHIPMENT_URL
} from '../data/siteData';
import { htmlToText, getPostImage } from '../utils/content';
import { getHomeLink } from '../utils/homeLinks';

export function HomePhoto({ photo, index, onOpen, className = '', loading = 'lazy' }) {
  return (
    <figure className={`fsf-home-media ${className}`.trim()}>
      <button
        className="fsf-home-photo-button"
        type="button"
        onClick={() => onOpen(index)}
        aria-label={`Open ${photo.alt}`}
      >
        <img src={photo.src} alt={photo.alt} loading={loading} />
        <span className="fsf-gallery__overlay fsf-home-photo-overlay" aria-hidden="true">
          View Photo
        </span>
      </button>
    </figure>
  );
}

export function HomeReviewCard({ review }) {
  return (
    <article className="fsf-home-review-card">
      <div className="fsf-home-review-card__header">
        <span className="fsf-home-review-card__avatar" aria-hidden="true">
          {review.name.charAt(0)}
        </span>
        <div>
          <h3>{review.name}</h3>
          <p>{review.source}</p>
        </div>
      </div>
      {review.rating ? (
        <div className="fsf-home-review-card__rating" aria-label={`${review.rating} out of 5 stars`}>
          <span aria-hidden="true">*****</span>
        </div>
      ) : null}
      <p className="fsf-home-review-card__text">{review.text}</p>
    </article>
  );
}

export function HomeReviewsSection() {
  return (
    <section className="fsf-home-reviews" aria-label="Customer reviews">
      <div className="fsf-home-reviews__intro">
        <div className="fsf-home-reviews__copy">
          <p className="fsf-section-kicker">Customer Reviews</p>
          <h2>Why choose Freight Services Fiji as your trusted service provider?</h2>
          <p>
            At <strong>Freight Services Fiji</strong>, quality is not optional. Every customer, whether just around the
            corner or across the globe, receives the same level of care, professionalism, and excellent service.
          </p>
          <p>
            From the heart of the South Pacific to partners and clients around the world, we move more than cargo. We
            move trust.
          </p>
          <a className="fsf-home-google-link" href={GOOGLE_BUSINESS_URL} target="_blank" rel="noopener noreferrer">
            <img src={HOME_IMAGES.googleBusiness} alt="Google My Business" loading="lazy" />
            <span>Open Google Business profile</span>
          </a>
          <a className="fsf-text-link" href={CUSTOMER_REVIEW_URL} target="_blank" rel="noopener noreferrer">
            Share your Freight Services Fiji experience
          </a>
        </div>
        <figure className="fsf-home-reviews__media">
          <img src={HOME_IMAGES.reviews} alt="Freight Services Fiji team member delivering customer cargo" loading="lazy" />
        </figure>
      </div>

      <div className="fsf-home-review-strip" role="list" aria-label="Customer review cards">
        {HOME_REVIEWS.map((review) => (
          <div role="listitem" key={review.name}>
            <HomeReviewCard review={review} />
          </div>
        ))}
      </div>
    </section>
  );
}

export function HomeServicePostCard({ post }) {
  const title = htmlToText(post.title?.rendered);
  const excerpt = htmlToText(post.excerpt?.rendered);
  const image = getPostImage(post);

  return (
    <article className="fsf-home-service-post">
      {image ? (
        <a className="fsf-home-service-post__image" href={post.link} aria-label={`Read ${title}`}>
          <img src={image} alt="" loading="lazy" />
        </a>
      ) : null}
      <div className="fsf-home-service-post__body">
        <h3>
          <a href={post.link}>{title}</a>
        </h3>
        {excerpt ? <p>{excerpt}</p> : null}
        <a className="fsf-text-link" href={post.link}>
          Read More
        </a>
      </div>
    </article>
  );
}

export function HomeCompanyIntroSection() {
  return (
    <section className="fsf-home-service-overview" aria-label="Freight Services Fiji company and services">
      <div className="fsf-home-service-overview__header">
        <p className="fsf-section-kicker">Connecting The Pacific To The World</p>
        <h2>Freight Services Fiji (PTE) Limited</h2>
      </div>
      <div className="fsf-home-service-overview__copy">
        <p>
          We are a professional logistics and moving service provider with branches and trusted partners across the
          South Pacific.
        </p>
        <p>
          Our Suva headquarters operates from Fiji's natural cargo hub, supporting local and international customers
          with freight, storage, transport, customs, and relocation services.
        </p>
      </div>
    </section>
  );
}

export function HomeTeamSlideshow() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const slideshowEnabled = window.BUSINESS_THEME?.slideshowEnabled !== '0';
  const configuredPhotos = window.BUSINESS_THEME?.slideshowPhotos;
  const photos = Array.isArray(configuredPhotos) ? configuredPhotos : HOME_SLIDESHOW_PHOTOS;
  const total = photos.length;

  useEffect(() => {
    if (isPaused || total < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % total);
    }, 6000);

    return () => window.clearInterval(interval);
  }, [isPaused, total]);

  const showSlide = (index) => {
    setActiveIndex((index + total) % total);
  };

  if (!slideshowEnabled || total === 0) {
    return null;
  }

  return (
    <section className="fsf-home-slideshow" aria-labelledby="fsf-home-slideshow-title">
      <div className="fsf-home-slideshow__header">
        <div>
          <p className="fsf-section-kicker">Our people and operations</p>
          <h2 id="fsf-home-slideshow-title">Freight Services Fiji in action</h2>
        </div>
        <p>A look at the people, facilities, and fleet that keep freight and moving services connected.</p>
      </div>

      <div className="fsf-home-slideshow__frame" role="region" aria-roledescription="carousel" aria-label="FSF staff and operations photos">
        <div className="fsf-home-slideshow__viewport" aria-live="off">
          {photos.map((photo, index) => (
            <figure
              className={`fsf-home-slideshow__slide${index === activeIndex ? ' is-active' : ''}`}
              aria-hidden={index !== activeIndex}
              key={photo.src}
            >
              <img
                className="fsf-home-slideshow__backdrop"
                src={photo.src}
                alt=""
                aria-hidden="true"
                loading={index < 2 ? 'eager' : 'lazy'}
              />
              <img
                className="fsf-home-slideshow__image"
                src={photo.src}
                alt={index === activeIndex ? photo.alt : ''}
                loading={index < 2 ? 'eager' : 'lazy'}
              />
              <figcaption>{photo.alt}</figcaption>
            </figure>
          ))}
        </div>

        <button
          className="fsf-home-slideshow__arrow fsf-home-slideshow__arrow--prev"
          type="button"
          onClick={() => showSlide(activeIndex - 1)}
          aria-label="Show previous slideshow image"
        >
          <span aria-hidden="true">&#8249;</span>
        </button>
        <button
          className="fsf-home-slideshow__arrow fsf-home-slideshow__arrow--next"
          type="button"
          onClick={() => showSlide(activeIndex + 1)}
          aria-label="Show next slideshow image"
        >
          <span aria-hidden="true">&#8250;</span>
        </button>
      </div>

      <div className="fsf-home-slideshow__controls">
        <div className="fsf-home-slideshow__dots" role="group" aria-label="Choose a slideshow image">
          {photos.map((photo, index) => (
            <button
              className={index === activeIndex ? 'is-active' : ''}
              type="button"
              onClick={() => showSlide(index)}
              aria-label={`Show image ${index + 1} of ${total}`}
              aria-current={index === activeIndex ? 'true' : undefined}
              key={photo.src}
            />
          ))}
        </div>
        <button
          className="fsf-home-slideshow__pause"
          type="button"
          onClick={() => setIsPaused((paused) => !paused)}
          aria-pressed={isPaused}
        >
          <span className="fsf-home-slideshow__pause-icon" aria-hidden="true">
            {isPaused ? '\u25B6' : '\u275A\u275A'}
          </span>
          <span>{isPaused ? 'Play slideshow' : 'Pause slideshow'}</span>
        </button>
      </div>
    </section>
  );
}

export function HomeServicePostsSection() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const restUrl = window.BUSINESS_THEME?.restUrl || '/wp-json/';
    const controller = new AbortController();
    const slugQuery = HOME_SERVICE_POST_SLUGS.join(',');

    fetch(`${restUrl}wp/v2/posts?per_page=12&slug=${slugQuery}&_embed=1`, {
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : []))
      .then((items) => {
        const bySlug = new Map(items.map((item) => [item.slug, item]));
        setPosts(HOME_SERVICE_POST_SLUGS.map((slug) => bySlug.get(slug)).filter(Boolean));
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setPosts([]);
        }
      });

    return () => controller.abort();
  }, []);

  return (
    <section className="fsf-home-service-overview fsf-home-service-overview--posts" aria-label="Freight Services Fiji service posts">
      <div className="fsf-home-service-overview__header">
        <p className="fsf-section-kicker">Service Updates</p>
        <h2>Explore FSF services</h2>
      </div>

      {posts.length ? (
        <div className="fsf-home-service-posts" aria-label="Freight Services Fiji service posts">
          {posts.map((post) => (
            <HomeServicePostCard post={post} key={post.id} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function HomeActionCards() {
  const quoteUrl = FSF_BRAND?.quoteUrl || window.BUSINESS_THEME?.ctaUrl || '/contactus/';

  const actions = [
    {
      title: 'Request a quote',
      text: 'Tell us what you need to move and our team will help shape the right logistics or relocation plan.',
      href: quoteUrl,
      label: 'Get Quote',
      external: quoteUrl.startsWith('http'),
    },
    {
      title: 'Track a shipment',
      text: 'Already moving with FSF? Check shipment progress and keep your planning moving.',
      href: TRACK_SHIPMENT_URL,
      label: 'Track Shipment',
      external: true,
    },
    {
      title: 'Share feedback',
      text: 'Finished a move or freight job with us? Your feedback helps our team keep improving.',
      href: CUSTOMER_REVIEW_URL,
      label: 'Customer Review Form',
      external: true,
    },
  ];

  return (
    <section className="fsf-home-action-grid" aria-label="Quick actions">
      {actions.map((action) => (
        <article className="fsf-home-action-card" key={action.title}>
          <div>
            <p className="fsf-section-kicker">{action.title}</p>
            <h3>{action.label}</h3>
            <p>{action.text}</p>
          </div>
          <a
            className="fsf-text-link"
            href={action.href}
            target={action.external ? '_blank' : undefined}
            rel={action.external ? 'noopener noreferrer' : undefined}
          >
            {action.label}
          </a>
        </article>
      ))}
    </section>
  );
}

export function HomeVideoSection() {
  return (
    <section className="fsf-home-video-section" aria-label="Freight Services Fiji video">
      <div className="fsf-home-video-section__header">
        <p className="fsf-section-kicker">FSF in motion</p>
        <h2>Connecting the Pacific through every movement</h2>
        <p>
          A closer look at the people, routes, and service culture behind Freight Services Fiji's logistics and moving
          work across the South Pacific.
        </p>
      </div>
      <VideoFrame
        url="https://youtu.be/Gmiht9ra0PE"
        title="Freight Services Fiji connecting the Pacific video"
        autoplay
        loop
        muted
        unmuteOnInteraction
      />
    </section>
  );
}

export default function HomePageSections() {
  const [activePhotoIndex, setActivePhotoIndex] = useState(null);
  const trackingUrl = getHomeLink('Click Here to Access', 'https://www.youtube.com/watch?v=tuPfyV9t5rc');
  const sustainabilityUrl = getHomeLink('Sustainability', '/corporate-social-responsibility-2/');

  return (
    <>
      <div className="fsf-home-sections">
        <section className="fsf-home-intro">
          <div className="fsf-home-intro__copy">
            <p className="fsf-section-kicker">#One team. One plan. Door to destination.</p>
            <h2>Bula Vinaka from Fiji's South Pacific logistics team</h2>
            <p>
              We coordinate freight, personal effects, vehicles, warehouse cargo, and relocation shipments across Fiji,
              the Pacific, and international trade lanes.
            </p>
            <p>
              Air, sea, road, warehousing, customs, and moving services come together through one practical point of
              coordination, from first enquiry to final delivery.
            </p>
            <p className="fsf-home-intro__tag">Together, we connect the Pacific to the world.</p>
          </div>
          <div className="fsf-home-intro__media">
            <HomePhoto photo={HOME_PHOTOS[0]} index={0} onOpen={setActivePhotoIndex} className="fsf-home-media--intro" loading="eager" />
          </div>
        </section>

        <HomeCompanyIntroSection />

        <HomeTeamSlideshow />

        <HomeActionCards />

        <section className="fsf-home-split">
          <HomePhoto photo={HOME_PHOTOS[1]} index={1} onOpen={setActivePhotoIndex} />
          <div className="fsf-home-split__copy">
            <p className="fsf-section-kicker">Quality Management</p>
            <h2>Quality management standard and control</h2>
            <p>
              At Freight Services Fiji, we are deeply committed to upholding the highest standards of quality in every
              aspect of our operations. Our dedication to excellence is reflected in rigorous internal processes,
              customer-centric service, and continuous improvement initiatives.
            </p>
            <p>
              We are proud to be ISO 9001:2015 Quality Management System certified and to hold the prestigious
              FIDI-FAIMPLUS certification, further affirming our capability to deliver world-class services with
              professionalism, consistency, and integrity.
            </p>
          </div>
        </section>

        <section className="fsf-home-band">
          <div>
            <p className="fsf-section-kicker">Digital Operations</p>
            <h2>Utilizing technology for productivity</h2>
          </div>
          <div className="fsf-home-band__copy">
            <p>
              At Freight Services Fiji, we leverage modern technology and digital tools to boost productivity and deliver
              faster, more efficient service. From real-time tracking to automated workflows, our tech-driven approach
              improves transparency and keeps operations moving.
            </p>
            <p>
              With just your shipment reference number, you can easily track the progress of your cargo in real time
              instead of having to contact us directly.
            </p>
            <a className="fsf-text-link" href={trackingUrl} target="_blank" rel="noopener noreferrer">
              Click Here to Access
            </a>
          </div>
        </section>

        <section className="fsf-home-sustainability">
          <div className="fsf-home-sustainability__header">
            <p className="fsf-section-kicker">Environmental Responsibility</p>
            <h2>The future is green</h2>
            <p>
              Freight Services Fiji is committed to environmental sustainability by promoting responsible logistics
              practices that reduce our carbon footprint. We continuously seek eco-friendly solutions, from optimizing
              delivery routes to minimizing waste and supporting greener technologies.
            </p>
          </div>

          <HomePhoto photo={HOME_PHOTOS[2]} index={2} onOpen={setActivePhotoIndex} className="fsf-home-media--feature" />

          <div className="fsf-home-sustainability__story">
            <div>
              <h3>Leading by example</h3>
              <p>
                The management team at Freight Services Fiji is committed to championing environmental responsibility.
                As part of our sustainability efforts, we have taken our message on the road by using delivery trucks as
                moving billboards to raise awareness about sustainable business practices.
              </p>
              <p>
                Through eye-catching visuals and bold messaging, we aim to inspire other businesses in Fiji to join us in
                driving real change toward a greener, more responsible future.
              </p>
            </div>
            <HomePhoto photo={HOME_PHOTOS[3]} index={3} onOpen={setActivePhotoIndex} />
          </div>

          <div className="fsf-home-cta">
            <p>If you wish to view our corporate and social responsibility statement, please click here:</p>
            <a className="fsf-button fsf-button--sustainability" href={sustainabilityUrl}>
              Sustainability
            </a>
          </div>
        </section>

        <HomeServicePostsSection />

        <HomeReviewsSection />

        <HomeVideoSection />
      </div>
      <PhotoLightbox
        images={HOME_PHOTOS}
        activeIndex={activePhotoIndex}
        setActiveIndex={setActivePhotoIndex}
        label="Freight Services Fiji homepage photos"
      />
    </>
  );
}
